import React from 'react';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import ImageIcon from '@mui/icons-material/Image';
import FolderZipIcon from '@mui/icons-material/FolderZip';
import InsertDriveFileIcon from '@mui/icons-material/InsertDriveFile';
import { formatUploadQueueTypeLabel } from '../../utils/fiscalComprobante';
import { resolveArcaDisplay } from '../../utils/arcaDisplay';

const LABEL = {
  validating: ['Validando…', 'pending'],
  rejected: ['Rechazado', 'error'],
  presigning: ['Preparando…', 'pending'],
  uploading: ['Subiendo…', 'pending'],
  upload_error: ['Error de subida', 'error'],
  queued: ['En cola de procesamiento…', 'pending'],
  duplicate: ['Posible duplicado', 'warn'],
  processing: ['Extrayendo datos…', 'pending'],
  reprocessing: ['Reprocesando…', 'pending'],
  needs_review: ['Revisar clasificación', 'warn'],
  done: ['✓ Procesado', 'success'],
  error: ['No se pudo procesar', 'error'],
};

const SUBTYPE_BADGE_CLASS = {
  INVOICE: 'upload-q-type-badge--invoice',
  DEBITO: 'upload-q-type-badge--debito',
  CREDITO: 'upload-q-type-badge--credito',
  OTROS: 'upload-q-type-badge--otros',
};

function fileIcon(name) {
  const lower = (name || '').toLowerCase();
  if (lower.endsWith('.pdf')) return <PictureAsPdfIcon fontSize="small" />;
  if (/\.(jpe?g|png)$/.test(lower)) return <ImageIcon fontSize="small" />;
  if (lower.endsWith('.zip')) return <FolderZipIcon fontSize="small" />;
  return <InsertDriveFileIcon fontSize="small" />;
}

function resolveSubtypeTag(item) {
  const label = formatUploadQueueTypeLabel(item);
  if (!label) return null;
  return {
    label,
    className: SUBTYPE_BADGE_CLASS[item.comprobante_subtype] || SUBTYPE_BADGE_CLASS.OTROS,
  };
}

export default function QueueItem({
  item,
  onRetry,
  onRemove,
  onOpen,
  onReclassify,
  onReprocess,
  canReprocess = false,
  arcaEnabled = false,
  canConstatar = false,
  onConstatar,
  arcaLoading = false,
}) {
  const [text, tone] = LABEL[item.status] || ['', 'pending'];
  const subtypeTag = resolveSubtypeTag(item);
  const arcaDisplay = resolveArcaDisplay(item.arca_validation, item, { enabled: arcaEnabled });
  const arcaTag = arcaDisplay ? [arcaDisplay.label, arcaDisplay.tone] : null;
  const indeterminate = ['queued', 'processing', 'reprocessing', 'presigning', 'validating'].includes(item.status);
  const showProgress = !['rejected', 'upload_error', 'error', 'duplicate', 'done', 'needs_review'].includes(
    item.status,
  );

  return (
    <div className="upload-q-item" data-tone={tone}>
      <div className={`upload-q-icon ${item.status === 'done' ? 'upload-q-icon--success' : ''}`}>
        {fileIcon(item.name)}
      </div>
      <div className="upload-q-info">
        <div className="upload-q-name" title={item.name}>{item.name}</div>
        <div className="upload-q-meta">
          {(item.size / 1e6).toFixed(2)} MB
          {item.status === 'duplicate' && item.duplicate_of && ' · ya existe'}
        </div>
        {item.status === 'done' && subtypeTag && (
          <span className={`upload-q-type-badge ${subtypeTag.className}`}>{subtypeTag.label}</span>
        )}
        {item.status === 'done' && arcaTag && (
          <span className={`upload-q-arca-badge upload-q-arca-badge--${arcaTag[1]}`}>{arcaTag[0]}</span>
        )}
        {item.status === 'done' && arcaDisplay?.showConstatar && canConstatar && onConstatar && (
          <button
            type="button"
            className="upload-q-action upload-q-action--arca"
            disabled={arcaLoading}
            onClick={() => onConstatar(item)}
          >
            {arcaLoading ? 'Validando…' : 'Constatar ARCA'}
          </button>
        )}
        {item.error && (item.status === 'rejected' || item.status === 'upload_error' || item.status === 'error') ? (
          <div className="upload-q-error">{item.error}</div>
        ) : showProgress ? (
          <div className={`upload-q-progress ${indeterminate ? 'indeterminate' : ''}`}>
            <i style={{ width: indeterminate ? undefined : `${item.pct}%` }} />
          </div>
        ) : null}
      </div>
      <span className={`upload-q-status ${tone}`}>{text}</span>
      {['upload_error', 'rejected', 'error'].includes(item.status) && item.file && (
        <button type="button" className="upload-q-action" onClick={() => onRetry(item.id)}>
          Reintentar
        </button>
      )}
      {item.status === 'done' && item.doc_id && (
        <>
          {canReprocess && onReprocess && (
            <button type="button" className="upload-q-action" onClick={() => onReprocess(item)}>
              Reprocesar
            </button>
          )}
          <button type="button" className="upload-q-action upload-q-action--primary" onClick={() => onOpen(item.doc_id)}>
            Ver
          </button>
        </>
      )}
      {item.status === 'error' && item.doc_id && canReprocess && onReprocess && (
        <button type="button" className="upload-q-action" onClick={() => onReprocess(item)}>
          Reprocesar
        </button>
      )}
      {item.status === 'needs_review' && item.doc_id && (
        <>
          <button type="button" className="upload-q-action upload-q-action--warn" onClick={() => onReclassify?.(item)}>
            Reclasificar
          </button>
          {canReprocess && onReprocess && (
            <button type="button" className="upload-q-action" onClick={() => onReprocess(item)}>
              Reprocesar
            </button>
          )}
          <button type="button" className="upload-q-action" onClick={() => onOpen(item.doc_id)}>
            Ver
          </button>
        </>
      )}
      {item.status === 'duplicate' && (item.duplicate_of || item.doc_id) && (
        <button
          type="button"
          className="upload-q-action"
          onClick={() => onOpen(item.duplicate_of || item.doc_id)}
        >
          Ver original
        </button>
      )}
      {!['uploading', 'presigning', 'queued', 'processing', 'reprocessing'].includes(item.status) && (
        <button type="button" className="upload-q-remove" onClick={() => onRemove(item.id)} aria-label="Quitar">
          ✕
        </button>
      )}
    </div>
  );
}
